// frontend/src/hooks/useCountUp.js
import { useState, useEffect, useRef } from 'react';

/**
 * Animates a number from 0 (or current value) to target over `duration` ms.
 * Uses requestAnimationFrame with cubic ease-out.
 *
 * @param {number} target   - Final value to animate to
 * @param {{ duration?: number, decimals?: number }} options
 * @returns {number}        - The currently animated value
 */
export default function useCountUp(target, { duration = 900, decimals = 0 } = {}) {
    const [value, setValue] = useState(0);
    const rafRef = useRef(null);
    const startRef = useRef(null);
    const fromRef = useRef(0);

    useEffect(() => {
        const num = Number(target);
        if (!Number.isFinite(num)) {
            setValue(0);
            return;
        }

        // If the new target equals current value, do nothing
        if (num === fromRef.current && value === num) return;

        const startValue = fromRef.current;
        const delta = num - startValue;
        const start = performance.now();
        startRef.current = start;

        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            // Cubic ease-out: 1 - (1 - t)^3
            const eased = 1 - Math.pow(1 - p, 3);
            const next = startValue + delta * eased;

            setValue(decimals > 0 ? Number(next.toFixed(decimals)) : Math.round(next));

            if (p < 1) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                fromRef.current = num;
            }
        };

        rafRef.current = requestAnimationFrame(tick);

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
        
    }, [target, duration, decimals]);

    return value;
}// JavaScript source code
